import { useState } from "react";
import { Loader2 } from "lucide-react";
import type { EstadoConexionFuente } from "../datos/estadoFuente";
import { CLASE_FOCO } from "./tarjetasFuente";

/**
 * Las dos piezas que dibujan el ESTADO de una fuente de datos: la píldora y los
 * botones.
 *
 * Nacieron dentro de `SeccionConexiones` (las tarjetas de "Fuentes de datos") y
 * se mudaron acá cuando apareció el segundo consumidor: el paso de conexiones
 * de la Puesta en marcha. Las dos pantallas contestan LA MISMA pregunta sobre
 * la misma cuenta y están a un clic de distancia — si una dice "No pudimos
 * consultarlo" y la otra "Sin conectar", una de las dos le está mintiendo al
 * usuario sobre su propia cuenta. Mismo motivo por el que las clases de las
 * tarjetas viven en `tarjetasFuente.ts`: si cambia una, cambian las dos.
 *
 * Los cuatro estados de una fuente con integración —cargando, conectado, sin
 * conectar, falló la consulta— se dibujan DISTINTO. Ver `datos/estadoFuente.ts`.
 *
 * Ver ADR-2026-08-17-estudio-marketing-puesta-en-marcha.
 */

export const CLASE_BOTON_PRIMARIO = `rounded-full bg-brand-primary py-2 px-4 text-[12.5px] font-medium text-white transition-opacity hover:opacity-90 ${CLASE_FOCO}`;
export const CLASE_BOTON_TEXTO = `rounded-full py-2 px-4 text-[12.5px] font-medium text-brand-primary transition-opacity hover:opacity-70 ${CLASE_FOCO}`;

export const PildoraEstado = ({ estado }: { estado: EstadoConexionFuente }) => {
  const base =
    "inline-flex items-center gap-1.5 rounded-full py-1 px-2.5 text-[11.5px] font-medium";
  if (estado === "conectado") {
    return (
      <span className={`${base} bg-[#DDEFE7] text-[#1D7A57]`}>
        <span className="h-1.5 w-1.5 rounded-full bg-[#1D7A57]" aria-hidden="true" />
        Conectado
      </span>
    );
  }
  if (estado === "error") {
    return <span className={`${base} bg-[#F8E3E2] text-[#C33B36]`}>No pudimos consultarlo</span>;
  }
  if (estado === "cargando") {
    return (
      <span className={`${base} bg-[#F0F2F7] text-[rgb(var(--brand-black-rgb)_/_0.55)]`}>
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
        Consultando…
      </span>
    );
  }
  if (estado === "no-disponible") {
    return <span className={`${base} bg-[#F0F2F7] text-[rgb(var(--brand-black-rgb)_/_0.45)]`}>Próximamente</span>;
  }
  /* "Sin conectar" NO se dibuja: al lado siempre hay un botón "Conectar", que
     dice exactamente lo mismo con la mitad del ruido. Lo sacó Matías por pedido
     explícito. NO REVERTIR. Los otros tres estados sí se muestran, porque
     ninguno es deducible del botón: "Conectado" no tiene botón de conectar,
     "No pudimos consultarlo" es lo contrario de una afirmación sobre la cuenta,
     y "Próximamente" no tiene botón ninguno. */
  return null;
};

export interface PropsAccionesFuente {
  estado: EstadoConexionFuente;
  /** Hay un "Reintentar" en vuelo (lo pidió el usuario recién, desde acá). */
  reintentando: boolean;
  onReintentar: () => void;
  /** Rótulo del primario cuando la fuente está sin conectar ("Conectar X"). */
  textoConectar: string;
  onConectar: () => void;
  /** El enlace a la pantalla donde vive esta conexión, en estado de error. */
  textoIr: string;
  onIr: () => void;
}

/**
 * Los botones de UNA tarjeta, con el PRIMARIO SIEMPRE MONTADO.
 *
 * Es la razón de ser de este componente y no es cosmético: el botón que se
 * aprieta es "Reintentar", y apretarlo cambia el estado de `error` a `cargando`.
 * Con un ternario que salta de una rama (Fragment con dos botones) a otra
 * (botón único), React borra el subárbol y monta otro: el nodo que tenía el
 * foco DESAPARECE y el foco cae al `<body>`. Un usuario de teclado que tabuló
 * hasta la tercera tarjeta vuelve al principio del documento y tiene que
 * rehacer todo el recorrido para ver el resultado de lo que acaba de pedir.
 * Manteniendo un único `<button>` en la misma posición —cambian el rótulo, el
 * handler y las clases, no el elemento— el DOM node se reusa y el foco se
 * queda donde estaba (ADR-2026-08-17-estudio-marketing-fixes-revision).
 *
 * Por el mismo motivo el primario NO se deshabilita mientras dura el reintento:
 * un elemento enfocado que pasa a `disabled` también pierde el foco (Chrome y
 * Firefox lo blurean al `<body>`). Se marca con `aria-busy` y el rótulo pasa a
 * "Reintentando…" — nunca a "Conectar X", que es la afirmación OPUESTA a la que
 * se está verificando: la fuente puede estar perfectamente conectada y el error
 * haber sido de red.
 */
export const AccionesFuente = ({
  estado,
  reintentando,
  onReintentar,
  textoConectar,
  onConectar,
  textoIr,
  onIr,
}: PropsAccionesFuente) => {
  const reintentoEnVuelo = estado === "cargando" && reintentando;
  /**
   * El segundo botón acompaña al "Reintentar": se monta en el error y sigue
   * montado durante el reintento.
   *
   * PERO CUANDO EL REINTENTO SALE BIEN las dos condiciones se apagan a la vez
   * (el estado pasa a `conectado`/`sin-conectar`) y React lo desmonta. Si el
   * usuario tabuló hasta él durante la ventana de red —que es exactamente lo
   * que hace quien está esperando la respuesta—, el nodo enfocado desaparece y
   * el foco cae al `<body>`: el mismo agujero que este componente cuida para el
   * primario, un botón más a la derecha. Por eso el botón también se queda
   * mientras TENGA el foco, y se desmonta recién en el `blur`, cuando el foco
   * ya se fue a otro lado por decisión del usuario (Tab, clic) y no hay nada
   * que perder. El costo es que, en ese rato, se ve un instante junto a
   * "Administrar conexión" — dos botones al mismo lugar, que es infinitamente
   * más barato que mandar a alguien de vuelta al principio del documento.
   */
  const [irEnfocado, setIrEnfocado] = useState(false);
  const muestraIr = estado === "error" || reintentoEnVuelo || irEnfocado;

  const primario = (() => {
    if (estado === "error") {
      return { texto: "Reintentar", onClick: onReintentar, clase: CLASE_BOTON_PRIMARIO,
               deshabilitado: false, ocupado: false };
    }
    if (reintentoEnVuelo) {
      return { texto: "Reintentando…", onClick: onReintentar, clase: CLASE_BOTON_PRIMARIO,
               deshabilitado: false, ocupado: true };
    }
    if (estado === "cargando") {
      // Carga inicial (nadie apretó nada): sin foco que cuidar, y el rótulo no
      // puede adelantar ninguna de las dos respuestas posibles.
      return { texto: "Consultando…", onClick: onConectar, clase: CLASE_BOTON_PRIMARIO,
               deshabilitado: true, ocupado: true };
    }
    if (estado === "conectado") {
      return { texto: "Administrar conexión", onClick: onIr, clase: CLASE_BOTON_TEXTO,
               deshabilitado: false, ocupado: false };
    }
    /* Visualmente sólo "Conectar" —el nombre de la fuente está a 30 cm, en la
       misma fila, y repetirlo es ruido— pero el `aria-label` conserva el rótulo
       completo: quien tabula a ciegas escucharía "Conectar, Conectar, Conectar"
       sin ninguna forma de saber cuál es cuál. Sacar una palabra de la pantalla
       es diseño; sacarla del lector es dejar a alguien sin poder usarla. */
    return { texto: "Conectar", etiqueta: textoConectar, onClick: onConectar,
             clase: CLASE_BOTON_PRIMARIO, deshabilitado: false, ocupado: false };
  })();

  return (
    <>
      <button
        type="button"
        onClick={primario.onClick}
        disabled={primario.deshabilitado}
        aria-busy={primario.ocupado || undefined}
        aria-label={primario.etiqueta}
        className={`${primario.clase} disabled:opacity-50`}
      >
        {primario.texto}
      </button>
      {muestraIr && (
        <button
          type="button"
          onClick={onIr}
          // Los dos handlers son los que sostienen el botón bajo el foco: no
          // son decorativos ni de estilo. Ver el comentario de `muestraIr`.
          onFocus={() => setIrEnfocado(true)}
          onBlur={() => setIrEnfocado(false)}
          className={CLASE_BOTON_TEXTO}
        >
          {textoIr}
        </button>
      )}
    </>
  );
};
